/*
 * section-article-looks.js
 * ------------------------------------------------------------
 * Small vanilla lightbox for the editorial "Looks" section. No dependency on
 * Splide or magnify.js (those are a carousel / product zoom respectively).
 *
 * Each [data-looks-section] wires its [data-looks-lightbox] images to its own
 * [data-looks-overlay]. Supports previous/next, Escape to close, backdrop click
 * to close, and restores focus to the trigger image on close.
 * ------------------------------------------------------------
 */
(function () {
  function initSection(section) {
    var overlay = section.querySelector('[data-looks-overlay]');
    var triggers = Array.prototype.slice.call(section.querySelectorAll('[data-looks-lightbox]'));
    if (!overlay || triggers.length === 0) return;

    // The drawer wiring returns an API so the lightbox bag button can open the
    // shop-the-look drawer for the current image's look, over the lightbox.
    var drawerApi = initDrawer(section);

    // Portal the lightbox to <body> too. Inside the section its `position: fixed`
    // and z-index are trapped in an ancestor stacking context, so z-index 10000
    // only competes locally and the sticky header (9999) paints over it. At the
    // document root it stacks above the header. (The drawer does the same.)
    if (overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
    }

    var imgEl = overlay.querySelector('[data-looks-img]');
    var prevBtn = overlay.querySelector('[data-looks-prev]');
    var nextBtn = overlay.querySelector('[data-looks-next]');
    var closeBtn = overlay.querySelector('[data-looks-close]');
    var bagBtn = overlay.querySelector('[data-looks-bag]');
    var dotsWrap = overlay.querySelector('[data-looks-dots]');

    var items = triggers.map(function (t) {
      return {
        src: t.getAttribute('data-full') || t.currentSrc || t.src,
        alt: t.getAttribute('alt') || '',
        focal: t.getAttribute('data-focal') || '50% 50%',
        lookKey: t.getAttribute('data-look-key') || null,
      };
    });

    var current = 0;
    var lastFocused = null;
    var multiple = items.length > 1;
    var fit = overlay.getAttribute('data-fit') || 'cover';
    var coverMedia = window.matchMedia('(min-width: 768px)');

    // Hide prev/next when there's only one image.
    if (!multiple) {
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
    }

    // Dot indicator: one dot per image; the current one is elongated (Kith-style).
    var dots = [];
    if (dotsWrap) {
      if (multiple) {
        items.forEach(function (item, index) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'looks-lightbox__dot';
          dot.setAttribute('aria-label', 'Go to image ' + (index + 1));
          dot.addEventListener('click', function () {
            current = index;
            render();
            positionScroll();
          });
          dotsWrap.appendChild(dot);
          dots.push(dot);
        });
      } else {
        dotsWrap.hidden = true;
      }
    }

    function drawerOpen() {
      return !!(drawerApi && drawerApi.isOpen && drawerApi.isOpen());
    }

    function render() {
      var item = items[current];
      if (!item) return;
      imgEl.setAttribute('src', item.src);
      imgEl.setAttribute('alt', item.alt);
      // Bag button only when the current image's look is shoppable.
      if (bagBtn) bagBtn.hidden = !item.lookKey;
      dots.forEach(function (dot, index) {
        dot.classList.toggle('is-active', index === current);
      });
    }

    // Desktop "cover" is fit-width-and-scroll: scroll the overlay so the current
    // image's focal point sits mid-viewport. Every other mode fits the image, so
    // reset to the top.
    function scrollToFocal() {
      var item = items[current];
      var focalY = 50;
      if (item && item.focal) {
        var parts = item.focal.split(/\s+/);
        var parsed = parts.length === 2 ? parseFloat(parts[1]) : NaN;
        if (!isNaN(parsed)) focalY = parsed;
      }
      var imgHeight = imgEl.offsetHeight || imgEl.naturalHeight || 0;
      var viewportHeight = overlay.clientHeight || window.innerHeight;
      var target = (focalY / 100) * imgHeight - viewportHeight / 2;
      var max = Math.max(0, imgHeight - viewportHeight);
      overlay.scrollTop = Math.min(Math.max(0, target), max);
    }

    function positionScroll() {
      imgEl.onload = null;
      if (fit === 'cover' && coverMedia.matches) {
        // Wait for the image so we can measure its rendered height.
        if (imgEl.complete && imgEl.naturalHeight) {
          scrollToFocal();
        } else {
          imgEl.onload = scrollToFocal;
        }
      } else {
        overlay.scrollTop = 0;
      }
    }

    function open(index) {
      current = index;
      lastFocused = document.activeElement;
      render();
      overlay.hidden = false;
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      positionScroll();
      document.addEventListener('keydown', onKeydown);
      (closeBtn || overlay).focus();
    }

    function close() {
      overlay.hidden = true;
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function step(delta) {
      if (!multiple) return;
      current = (current + delta + items.length) % items.length;
      render();
      positionScroll();
    }

    function onKeydown(event) {
      // While the shop-the-look drawer is open over the lightbox, let it own keys.
      if (drawerOpen()) return;
      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'ArrowLeft') {
        step(-1);
      } else if (event.key === 'ArrowRight') {
        step(1);
      }
    }

    // Re-apply layout + scroll position when crossing the desktop breakpoint.
    function onBreakpointChange() {
      render();
      positionScroll();
    }
    if (coverMedia.addEventListener) {
      coverMedia.addEventListener('change', onBreakpointChange);
    } else if (coverMedia.addListener) {
      coverMedia.addListener(onBreakpointChange);
    }

    triggers.forEach(function (trigger, index) {
      trigger.style.cursor = 'zoom-in';
      trigger.addEventListener('click', function () {
        open(index);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    // Bag button: open the shop-the-look drawer for the current image's look
    // over the lightbox; closing the drawer returns to the still-open lightbox.
    if (bagBtn) {
      bagBtn.addEventListener('click', function () {
        var item = items[current];
        if (item && item.lookKey && drawerApi && typeof drawerApi.open === 'function') {
          drawerApi.open(item.lookKey);
        }
      });
    }

    // Click on the backdrop (not the image or a control) closes.
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) close();
    });

    // Touch swipe left/right to move between images (mobile).
    var touchStartX = 0;
    var touchStartY = 0;
    var touchActive = false;
    overlay.addEventListener(
      'touchstart',
      function (event) {
        if (drawerOpen() || event.touches.length !== 1) return;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        touchActive = true;
      },
      { passive: true }
    );
    overlay.addEventListener(
      'touchend',
      function (event) {
        if (!touchActive) return;
        touchActive = false;
        var touch = event.changedTouches[0];
        var dx = touch.clientX - touchStartX;
        var dy = touch.clientY - touchStartY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          step(dx < 0 ? 1 : -1);
        }
      },
      { passive: true }
    );
  }

  /* ---------- Shop-the-look drawer ---------- */
  function initDrawer(section) {
    var drawer = section.querySelector('[data-look-drawer]');
    if (!drawer) return;

    var openers = Array.prototype.slice.call(section.querySelectorAll('[data-look-open]'));
    if (openers.length === 0) return;

    // Portal the drawer to <body>, matching the theme's own cart-drawer, so its
    // `position: fixed` always resolves against the viewport and its z-index is
    // root-level regardless of any transformed ancestor. (Note: the scrim being
    // invisible was actually caused by base.css `div:empty { display: none }`
    // hiding the empty scrim <div>; that's fixed in CSS. This portal is kept as
    // a robustness safeguard.)
    if (drawer.parentNode !== document.body) {
      document.body.appendChild(drawer);
    }

    var panel = drawer.querySelector('.look-drawer__panel');
    var contents = Array.prototype.slice.call(drawer.querySelectorAll('[data-look-content]'));
    var closers = Array.prototype.slice.call(drawer.querySelectorAll('[data-look-drawer-close]'));

    var lastFocused = null;
    var hideTimer = null;

    function showContent(key) {
      contents.forEach(function (content) {
        content.hidden = content.getAttribute('data-look-content') !== key;
      });
    }

    // Only elements that are actually visible (i.e. inside the shown look panel).
    function getFocusable() {
      var selector = 'a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])';
      return Array.prototype.slice.call(panel.querySelectorAll(selector)).filter(function (el) {
        return el.offsetParent !== null;
      });
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function openDrawer(key) {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      showContent(key);
      lastFocused = document.activeElement;
      drawer.hidden = false;
      // Force reflow so the transform transition runs from the hidden state.
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', onKeydown);
      var focusable = getFocusable();
      (focusable[0] || panel).focus();
    }

    function closeDrawer() {
      if (drawer.hidden) return;
      drawer.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown);
      // Only release the shared scroll lock if no other drawer is open.
      if (!document.querySelector('side-menu.active, cart-drawer.active')) {
        document.body.classList.remove('overflow-hidden');
      }
      hideTimer = setTimeout(function () {
        drawer.hidden = true;
        hideTimer = null;
      }, 350);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        // The bag button sits over the image; don't let it trigger the lightbox.
        event.preventDefault();
        event.stopPropagation();
        openDrawer(btn.getAttribute('data-look-open'));
      });
    });

    closers.forEach(function (btn) {
      btn.addEventListener('click', closeDrawer);
    });

    // --- Add to cart, in-drawer ------------------------------------------
    // We deliberately do NOT use the theme's <product-form>, because it always
    // renders the cart drawer open on success. Instead we post to the cart the
    // same way the theme does, refresh the header cart count, flash the button,
    // and reveal a "View cart" link — leaving this drawer open the whole time.
    var viewCartLink = drawer.querySelector('.look-drawer__view-cart');

    // "View cart" opens the theme's cart drawer (closing this one first) rather
    // than navigating to /cart. The href stays as a no-JS fallback.
    if (viewCartLink) {
      viewCartLink.addEventListener('click', function (event) {
        var cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer || typeof cartDrawer.open !== 'function') return; // fall back to the href
        event.preventDefault();
        closeDrawer();
        cartDrawer.open();
      });
    }

    // The section IDs the theme's cart-drawer re-renders itself with
    // (`cart-drawer` for #CartDrawer + `cart-icon-bubble` for the header count).
    // Read them from the element so we stay in sync if the theme changes them.
    function getCartSections() {
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        return cartDrawer.getSectionsToRender();
      }
      return [
        // Must match cart-drawer.js CartDrawer.getSectionsToRender(): only the
        // dynamic region is replaced, keeping the static express block intact.
        { id: 'cart-drawer', selector: '#CartDrawer-Main' },
        { id: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
      ];
    }

    // Update the cart drawer + header count the same way cart-drawer.js's
    // renderContents() does — reusing its own getSectionsToRender() and
    // getSectionInnerHTML() — but WITHOUT calling open(), so the cart stays shut.
    function refreshCartSections(response) {
      if (!response || !response.sections) return;
      var cartDrawer = document.querySelector('cart-drawer');
      // Match renderContents(): clear the empty state before injecting items.
      var inner = cartDrawer ? cartDrawer.querySelector('.drawer__inner') : null;
      if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');

      getCartSections().forEach(function (section) {
        var html = response.sections[section.id];
        if (html == null) return;
        var element = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!element) return;
        if (cartDrawer && typeof cartDrawer.getSectionInnerHTML === 'function') {
          element.innerHTML = cartDrawer.getSectionInnerHTML(html, section.selector);
        } else {
          var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (parsed) element.innerHTML = parsed.innerHTML;
        }
      });

      // #CartDrawer-Overlay lives inside #CartDrawer, so replacing that section's
      // markup drops its click-to-close listener. Re-bind it as renderContents()
      // does (its setTimeout re-attaches this before calling open()).
      if (cartDrawer) {
        var overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
      }
    }

    function flashAdded(button) {
      var textEl = button.querySelector('.look-drawer__add-text') || button;
      if (button._originalAddText == null) button._originalAddText = textEl.textContent;
      if (button._addedTimer) clearTimeout(button._addedTimer);
      textEl.textContent = 'Added';
      button.classList.add('is-added');
      button._addedTimer = setTimeout(function () {
        textEl.textContent = button._originalAddText;
        button.classList.remove('is-added');
        button._addedTimer = null;
      }, 2000);
    }

    function onAddSubmit(event) {
      var form = event.target && event.target.closest ? event.target.closest('.look-drawer__product-form') : null;
      if (!form) return;
      event.preventDefault();

      var button = form.querySelector('.look-drawer__add');
      var errorEl = form.querySelector('.look-drawer__error');
      var spinner = button ? button.querySelector('.loading-overlay__spinner') : null;
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');

      var config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      var formData = new FormData(form);
      // Request the cart drawer's own section(s) so we can refresh its contents
      // without a page reload — same set the theme uses when it re-renders.
      var sectionIds = getCartSections().map(function (section) {
        return section.id;
      });
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      var addUrl = window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add.js';

      fetch(addUrl, config)
        .then(function (res) {
          return res.json();
        })
        .then(function (response) {
          if (response.status) {
            // Theme-style error handling for a failed add.
            if (errorEl) {
              errorEl.textContent = response.description || response.message || '';
              errorEl.hidden = false;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'look-drawer',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            return;
          }
          refreshCartSections(response);
          flashAdded(button);
          if (viewCartLink) viewCartLink.hidden = false;
        })
        .catch(function (err) {
          console.error(err); // eslint-disable-line no-console
          if (errorEl) {
            errorEl.textContent = '';
            errorEl.hidden = true;
          }
        })
        .finally(function () {
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
          button.removeAttribute('aria-disabled');
        });
    }

    drawer.addEventListener('submit', onAddSubmit);

    // Expose a small API so the lightbox can open the matching look over itself.
    return {
      open: openDrawer,
      close: closeDrawer,
      isOpen: function () {
        return drawer.classList.contains('is-open');
      },
    };
  }

  function init() {
    var sections = document.querySelectorAll('[data-looks-section]');
    Array.prototype.forEach.call(sections, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
